// Underwriting Section 7: Loan Document Issuance.
//
// Two document bundles, tracked as a plain internal record — no
// certifications, no audit-log ceremony:
//   - Buyer Loan Documents  (buyer portal)   → issuedAt / acknowledgedAt /
//                                               signedDocumentsReceivedAt
//   - Seller Loan Documents (seller portal;  → sellerIssuedAt /
//     includes related exhibits)               sellerAcknowledgedAt /
//                                               sellerSignedDocumentsReceivedAt
//
// Completing these dates does not trigger any portal-side document delivery
// or notification — the section is an internal record only. Section-7
// completion (used by the accordion gate + Overview count) = both bundles
// have Documents Issued + Acknowledged dates.
//
// A date saves when the field is left (blur or Enter), not on each change: a
// date input reports every complete date while one is typed, so saving on
// change stored years like 0002 and took focus away mid-entry (staging run,
// 2026-10-15). An emptied field clears the date. Saves run one after another,
// each built from the last saved row, because every save sends all six dates
// and the server replaces them (owner ruling, 2026-10-15). The server refuses
// a date after today or before the deal was created; the field reverts.

import { useEffect, useRef, useState } from "react";
import { Card, FormField, Input } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  useLoanDocumentIssuance,
  useUpsertLoanDocumentIssuance,
} from "@/hooks/dealFlow/useDealFlow";
import type { LoanDocumentIssuanceDto } from "@/api/dealFlow/types";
import { calendarDateInputToIso, operationalToday } from "@/utils/datetime";

interface LoanDocumentIssuanceSectionProps {
  dealId: string;
  /** The deal's creation instant; no date may precede its Phoenix day. */
  dealCreatedAt: string;
}

type IssuanceDateField =
  | "issuedAt"
  | "acknowledgedAt"
  | "signedDocumentsReceivedAt"
  | "sellerIssuedAt"
  | "sellerAcknowledgedAt"
  | "sellerSignedDocumentsReceivedAt";

export function LoanDocumentIssuanceSection({
  dealId,
  dealCreatedAt,
}: LoanDocumentIssuanceSectionProps) {
  const row = useLoanDocumentIssuance(dealId);
  const upsert = useUpsertLoanDocumentIssuance(dealId);
  const toast = useToast();

  // The row each save is built from: the last one the server returned, or the
  // query's when no save is running.
  const latest = useRef<LoanDocumentIssuanceDto | null | undefined>(row.data);
  const saving = useRef(0);
  useEffect(() => {
    if (saving.current === 0) latest.current = row.data;
  }, [row.data]);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const minDate = operationalToday(new Date(dealCreatedAt));
  const maxDate = operationalToday();

  const onDateCommit = (field: IssuanceDateField, value: string): Promise<boolean> => {
    const save = async (): Promise<boolean> => {
      const base = latest.current;
      try {
        latest.current = await upsert.mutateAsync({
          issuedAt: base?.issuedAt ?? null,
          acknowledgedAt: base?.acknowledgedAt ?? null,
          signedDocumentsReceivedAt: base?.signedDocumentsReceivedAt ?? null,
          sellerIssuedAt: base?.sellerIssuedAt ?? null,
          sellerAcknowledgedAt: base?.sellerAcknowledgedAt ?? null,
          sellerSignedDocumentsReceivedAt: base?.sellerSignedDocumentsReceivedAt ?? null,
          [field]: calendarDateInputToIso(value),
        });
        return true;
      } catch (err) {
        toast.error(
          err instanceof ApiError && err.errors.length
            ? err.errors.join("; ")
            : "Could not update issuance dates.",
        );
        return false;
      } finally {
        saving.current -= 1;
      }
    };
    saving.current += 1;
    const result = queue.current.then(save);
    queue.current = result;
    return result;
  };

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 7 — Loan Document Issuance
        </h3>
        <p className="text-caption text-gray-mid m-0">
          B1 issues two loan-document bundles — one to the buyer through the
          buyer portal, one to the seller (including all related exhibits)
          through the seller portal — and records issuance, acknowledgement, and
          receipt of signed copies. Internal record only.
        </p>
      </header>

      <Bundle
        title="Buyer Loan Documents"
        acknowledgedLabel="Borrower acknowledged"
        issuedValue={row.data?.issuedAt}
        acknowledgedValue={row.data?.acknowledgedAt}
        signedValue={row.data?.signedDocumentsReceivedAt}
        minDate={minDate}
        maxDate={maxDate}
        onCommit={(kind, value) =>
          onDateCommit(
            kind === "issued"
              ? "issuedAt"
              : kind === "acknowledged"
                ? "acknowledgedAt"
                : "signedDocumentsReceivedAt",
            value,
          )
        }
      />

      <Bundle
        title="Seller Loan Documents"
        acknowledgedLabel="Seller acknowledged"
        issuedValue={row.data?.sellerIssuedAt}
        acknowledgedValue={row.data?.sellerAcknowledgedAt}
        signedValue={row.data?.sellerSignedDocumentsReceivedAt}
        minDate={minDate}
        maxDate={maxDate}
        onCommit={(kind, value) =>
          onDateCommit(
            kind === "issued"
              ? "sellerIssuedAt"
              : kind === "acknowledged"
                ? "sellerAcknowledgedAt"
                : "sellerSignedDocumentsReceivedAt",
            value,
          )
        }
      />
    </Card>
  );
}

interface BundleProps {
  title: string;
  acknowledgedLabel: string;
  issuedValue: string | null | undefined;
  acknowledgedValue: string | null | undefined;
  signedValue: string | null | undefined;
  minDate: string;
  maxDate: string;
  onCommit: (kind: "issued" | "acknowledged" | "signed", value: string) => Promise<boolean>;
}

function Bundle({
  title,
  acknowledgedLabel,
  issuedValue,
  acknowledgedValue,
  signedValue,
  minDate,
  maxDate,
  onCommit,
}: BundleProps) {
  return (
    <div className="mb-space-4 last:mb-0">
      <h4 className="font-display text-label uppercase tracking-label text-gold-shadow mb-space-2">
        {title}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <IssuanceDateInput
          label="Documents issued"
          stored={issuedValue}
          minDate={minDate}
          maxDate={maxDate}
          onCommit={(value) => onCommit("issued", value)}
        />
        <IssuanceDateInput
          label={acknowledgedLabel}
          stored={acknowledgedValue}
          minDate={minDate}
          maxDate={maxDate}
          onCommit={(value) => onCommit("acknowledged", value)}
        />
        <IssuanceDateInput
          label="Signed docs received"
          stored={signedValue}
          minDate={minDate}
          maxDate={maxDate}
          onCommit={(value) => onCommit("signed", value)}
        />
      </div>
    </div>
  );
}

/** One date: edited locally, saved when left if it changed, reverted when the
 *  save is refused. Stays enabled while a save runs, so typing is never cut off. */
function IssuanceDateInput({
  label,
  stored,
  minDate,
  maxDate,
  onCommit,
}: {
  label: string;
  stored: string | null | undefined;
  minDate: string;
  maxDate: string;
  onCommit: (value: string) => Promise<boolean>;
}) {
  const storedValue = toDateInputValue(stored);
  const [draft, setDraft] = useState(storedValue);
  useEffect(() => setDraft(storedValue), [storedValue]);
  // The value being saved, so the blur that follows Enter does not save it twice.
  const sending = useRef<string | null>(null);

  const commit = async (input: HTMLInputElement) => {
    // A half-typed date reads as "" with badInput set; that is not a clear.
    if (input.validity.badInput) return;
    if (draft === storedValue || draft === sending.current) return;
    sending.current = draft;
    const saved = await onCommit(draft);
    sending.current = null;
    if (!saved) setDraft(storedValue);
  };

  return (
    <FormField label={label}>
      <Input
        type="date"
        value={draft}
        min={minDate}
        max={maxDate}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => void commit(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit(e.currentTarget);
        }}
      />
    </FormField>
  );
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}
